import { Injectable } from '@angular/core';
import { clientLegalName } from '@core/util/client';
import {
	getMortgageLenderNamesForTitleInsurance,
	getMortgageName,
} from '@core/util/mortgage';
import { isSet } from '@core/util/value';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import { Encumbrance } from '@models/encumbrance';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import {
	BusinessMatterClient,
	Matter,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PropertyType,
} from '@models/matter';
import { MortgageeType } from '@models/lender';
import { isEmpty } from 'lodash';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';

export interface MissingFieldSection {
	sectionName: string;
	fields: MissingField[];
}

export interface MissingField {
	openDrawer?: () => void;
	url?: string;
	fieldName: string;
}

@Injectable({
	providedIn: 'root',
})
export class ValidateTitlePLUSService {
	constructor(private drawerService: DrawerService) {}

	getSubmitDataMissingSections(
		matter: Matter,
		clients: (PersonMatterClient | BusinessMatterClient)[],
		mortgages: MatterMortgageWithAssociations[],
		remainEncumbrances: Encumbrance[],
		nameFormattingSettings: NameFormattingSettings,
		matterTitles: MatterTitle[],
		phoneNumber: string,
		lawyerConnected: boolean,
	): MissingFieldSection[] {
		const sections: MissingFieldSection[] = [];

		if (!phoneNumber) {
			const profileSection: MissingFieldSection = {
				sectionName: 'Profile details',
				fields: [
					{
						fieldName: 'No phone number on your profile',
						url: UrlService.Profile(),
					},
				],
			};

			sections.push(profileSection);
		}

		if (!lawyerConnected) {
			const lawyerSection: MissingFieldSection = {
				sectionName: 'Lawyer details',
				fields: [
					{
						fieldName: 'Lawyer needs to connect to TitlePLUS',
						url: UrlService.Profile_Integrations_TitlePLUS(),
					},
				],
			};

			sections.push(lawyerSection);
		}

		const transactionSection: MissingFieldSection = {
			sectionName: 'Transaction details',
			fields: this.getTransactionMissingFields(matter),
		};

		if (transactionSection.fields.length > 0) {
			sections.push(transactionSection);
		}

		const clientSection: MissingFieldSection = {
			sectionName: `${matter.fileType === MatterFileType.PURCHASE ? 'Purchaser' : 'Mortgagor'} details`,
			fields: [],
		};

		if (clients.length === 0) {
			if (matter.fileType === MatterFileType.PURCHASE) {
				clientSection.fields.push({
					fieldName: 'No purchaser on file',
					url: UrlService.Matter_Parties(matter.id),
				});
			}
			if (matter.fileType === MatterFileType.REFINANCE) {
				clientSection.fields.push({
					fieldName: 'No mortgagor on file',
					url: UrlService.Matter_Parties_Mortgagors(matter.id),
				});
			}
		}

		if (clientSection.fields.length > 0) {
			sections.push(clientSection);
		}

		clients.forEach((client) => {
			const clientMissingSection: MissingFieldSection = {
				sectionName: `Client -${clientLegalName(client, nameFormattingSettings)}`,
				fields: this.getClientMissingFields(matter.id, client),
			};

			if (clientMissingSection?.fields?.length > 0) {
				sections.push(clientMissingSection);
			}
		});

		const propertySection: MissingFieldSection = {
			sectionName: 'Property details',
			fields: this.getPropertyMissingFields(matter, matterTitles),
		};

		if (propertySection.fields.length > 0) {
			sections.push(propertySection);
		}

		if (
			matter?.fileType === MatterFileType.REFINANCE &&
			mortgages.length === 0
		) {
			sections.push({
				sectionName: 'Mortgage details',
				fields: [
					{
						fieldName: 'No mortgage on the file',
						url: UrlService.Matter_Mortgages(matter.id),
					},
				],
			});
		}

		mortgages
			.filter((x) => isSet(x?.priority))
			.forEach((mortgage) => {
				const mortgageMissingSection: MissingFieldSection = {
					sectionName: getMortgageName(mortgage),
					fields: this.getMortgageMissingFields(matter?.id, mortgage),
				};

				if (mortgageMissingSection?.fields?.length > 0) {
					sections.push(mortgageMissingSection);
				}
			});

		remainEncumbrances?.forEach((encumbrance: Encumbrance) => {
			const encumbranceMissingSection: MissingFieldSection = {
				sectionName: `Encumbrance -${encumbrance.priority}`,
				fields: this.getEncumbranceMissingFields(matter?.id, encumbrance),
			};

			if (encumbranceMissingSection?.fields?.length > 0) {
				sections.push(encumbranceMissingSection);
			}
		});

		return sections;
	}

	private getEncumbranceMissingFields(
		matterId: string,
		encumbrance: Encumbrance,
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Encumbrances(matterId);

		if (!isSet(encumbrance.mortgageeType)) {
			fields.push({
				fieldName: 'Mortgagee type',
				url,
			});
		} else if (encumbrance.mortgageeType === MortgageeType.INSTITUTION) {
			if (!isSet(encumbrance.financialInstitution?.name)) {
				fields.push({
					fieldName: 'Lender name',
					url,
				});
			}
		} else if (encumbrance.mortgageeType === MortgageeType.PRIVATE_LENDER) {
			if (!isSet(encumbrance.privateLender?.fullName)) {
				fields.push({
					fieldName: 'Lender name',
					url,
				});
			}
		}

		return fields;
	}

	private getClientMissingFields(
		matterId: string,
		client: PersonMatterClient | BusinessMatterClient,
	): MissingField[] {
		const fields: MissingField[] = [];
		const openDrawer = () => {
			this.drawerService.openDrawer(GlobalProfileComponent, {
				data: { clientId: client.id },
			});
		};

		if (client.type === ClientType.PERSON) {
			if (!isSet((client as PersonClient)?.lastName)) {
				fields.push({
					fieldName: 'Last name',
					openDrawer,
				});
			}
		} else if (client.type === ClientType.CORPORATION) {
			if (!isSet((client as BusinessClient)?.name)) {
				fields.push({
					fieldName: 'Company name',
					openDrawer,
				});
			}
		}

		if (client?.serviceAddress) {
			if (!isSet(client.serviceAddress?.street)) {
				fields.push({
					fieldName: 'Service address - street',
					openDrawer,
				});
			}

			if (!isSet(client.serviceAddress?.province)) {
				fields.push({
					fieldName: 'Service address - province',
					openDrawer,
				});
			}
		}

		return fields;
	}

	private getMortgageMissingFields(
		matterId: string,
		mortgage: MatterMortgageWithAssociations,
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Mortgages_Mortgage(matterId, mortgage.id);
		const allLenderNames = getMortgageLenderNamesForTitleInsurance(mortgage);
		if (!allLenderNames) {
			fields.push({
				fieldName: 'Lender name',
				url,
			});
		}

		if (!isSet(mortgage?.amount)) {
			fields.push({
				fieldName: 'Amount',
				url,
			});
		} else if (mortgage?.amount === 0) {
			fields.push({
				fieldName: "Amount can't be $0.00",
				url,
			});
		} else if (mortgage?.amount < 100000 || mortgage?.amount > 500000000) {
			fields.push({
				fieldName:
					'Outstanding amount must be greater than $1000 and less than $5 million',
				url,
			});
		}

		if (!isSet(mortgage?.referenceNumber)) {
			fields.push({
				fieldName: 'Reference number',
				url,
			});
		}

		return fields;
	}

	private getTransactionMissingFields(matter: Matter): MissingField[] {
		const fields: MissingField[] = [];

		if (!isSet(matter.lawyerAssigned)) {
			fields.push({
				fieldName: 'No lawyer assigned',
				url: UrlService.Matter_Overview(matter.id),
			});
		}

		if (!isSet(matter.closingDate)) {
			fields.push({
				fieldName: 'Closing date',
				url: UrlService.Matter_TitleInsurance(matter.id),
			});
		}

		return fields;
	}

	private getPropertyMissingFields(
		matter: Matter,
		matterTitles: MatterTitle[],
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Property_Details(matter.id);

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (!isSet(matter.purchasePrice) || matter.purchasePrice === 0) {
				fields.push({
					fieldName: 'Purchase price',
					url: UrlService.Matter_Adjustments(matter.id),
				});
			}
		}

		if (isSet(matter.propertyDetails)) {
			if (!isSet(matter?.propertyDetails?.address?.street)) {
				fields.push({
					fieldName: 'Property address - street',
					url,
				});
			}

			if (!isSet(matter?.propertyDetails?.propertyType)) {
				fields.push({
					fieldName: 'Property type',
					url,
				});
			}
			if (!isSet(matter?.propertyDetails?.address?.province)) {
				fields.push({
					fieldName: 'Property address - province',
					url,
				});
			}
			if (!isSet(matter?.propertyDetails?.address?.postcode)) {
				fields.push({
					fieldName: 'Property address - postal code',
					url,
				});
			}
			if (!isSet(matter?.propertyDetails?.address?.city)) {
				fields.push({
					fieldName: 'Property address - city',
					url,
				});
			}

			if (!isSet(matter?.propertyDetails?.lro?.selected?.id)) {
				fields.push({
					fieldName: 'LRO',
					url,
				});
			}

			if (matterTitles.length === 0) {
				if (
					matter?.propertyDetails?.propertyType?.type === PropertyType.CONDO &&
					isEmpty(matterTitles)
				) {
					fields.push({
						fieldName: 'PIN',
						url,
					});
				} else if (
					matter?.propertyDetails?.propertyType?.type !== PropertyType.CONDO
				) {
					fields.push({
						fieldName: 'PIN',
						url,
					});
				}
			} else {
				matterTitles.forEach((matterTitle) => {
					if (!isSet(matterTitle?.pinNumber)) {
						fields.push({
							fieldName: 'PIN',
							url,
						});
					}
					if (!isSet(matterTitle?.legalDescription)) {
						fields.push({
							fieldName: `Legal description (${matterTitle?.pinNumber})`,
							url,
						});
					}
				});
			}
		} else {
			fields.push({
				fieldName: 'No property on file',
				url,
			});
		}

		return fields;
	}
}
